/** ShopScreen — Color パック購入モーダル(Phase A モック)。
 *  Reference: CLAUDE.md §5.2 スコープ厳守
 *  - 実 IAP なし:タップで Color をローカル付与、「デモ:実購入は行われません」表示
 *  - 非ゴール遵守:Pay-to-Win なし、ヘルパーアイテムは課金で買えない
 *    (Skill Honored:広告/デイリー/ステージクリアのみでヘルパー入手)
 *  - Premium/Royal パックには Color + helpers を付与(Phase A デモ用、本番では Color のみ)
 *  - document.body に直接マウント(Home 画面の上にモーダルで乗せる)
 *
 *  Callers:
 *    - web/src/main.ts: new ShopScreen({ ... })
 *    - web/src/ui/screens/HomeScreen.ts: onOpenShop callback → shopScreen.open()
 */

import { InventoryStore, HELPER_IDS, HELPER_META, type HelperId } from '../../game/InventoryStore';
import { BoosterStore, BOOSTER_IDS, BOOSTER_META, type BoosterId } from '../../game/BoosterStore';
import { showConfirmDialog } from '../ConfirmDialog';

const BOOSTER_SHOP_PRICE = 250; // Color per booster unit

export interface ShopScreenOptions {
  readonly getColorBalance: () => number;
  readonly addColor: (amount: number) => void;
  readonly inventoryStore: InventoryStore;
  readonly boosterStore?: BoosterStore;
  readonly onClose: () => void;
  /** Watch Ad ボタンが押されたとき呼ぶ(AdMockOverlay 表示)。成功=true */
  readonly onWatchAd?: () => Promise<boolean>;
}

interface PackDef {
  readonly id: string;
  readonly icon: string;
  readonly name: string;
  readonly colorAmount: number;
  readonly price: string;
  /** null = Color パックのみ */
  readonly helperCount: number | null;
  readonly featured: 'premium' | 'royal' | null;
  readonly badgeLabel: string | null;
}

/** LS key: 当日受取済みフラグ。値は 'YYYY-MM-DD' */
const LS_DAILY_SHOP = 'ck.dailyShopBonus';
/** LS key: 当日広告視聴済みフラグ。値は 'YYYY-MM-DD' */
const LS_AD_SHOP = 'ck.adShopLastClaim';

const PACKS: readonly PackDef[] = [
  {
    id: 'premium',
    icon: '💎',
    name: '至高の財宝',
    colorAmount: 65000,
    price: '¥15,000',
    helperCount: 5,
    featured: 'premium',
    badgeLabel: 'ベストバリュー',
  },
  {
    id: 'royal',
    icon: '👑',
    name: 'ロイヤルトレジャー',
    colorAmount: 25000,
    price: '¥4,800',
    helperCount: 3,
    featured: 'royal',
    badgeLabel: 'おすすめ',
  },
  { id: 'pack-1k',   icon: '🪙', name: 'Small',  colorAmount: 1000,   price: '¥300',    helperCount: null, featured: null, badgeLabel: null },
  { id: 'pack-5k',   icon: '🪙', name: 'Medium', colorAmount: 5000,   price: '¥1,200',  helperCount: null, featured: null, badgeLabel: null },
  { id: 'pack-10k',  icon: '🪙', name: 'Large',  colorAmount: 10000,  price: '¥2,200',  helperCount: null, featured: null, badgeLabel: null },
  { id: 'pack-25k',  icon: '🪙', name: 'XL',     colorAmount: 25000,  price: '¥4,500',  helperCount: null, featured: null, badgeLabel: null },
  { id: 'pack-50k',  icon: '🪙', name: 'XXL',    colorAmount: 50000,  price: '¥8,000',  helperCount: null, featured: null, badgeLabel: null },
  { id: 'pack-100k', icon: '🪙', name: 'Mega',   colorAmount: 100000, price: '¥15,000', helperCount: null, featured: null, badgeLabel: null },
];

export class ShopScreen {
  private readonly overlay: HTMLDivElement;
  private readonly options: ShopScreenOptions;
  private readonly boosterStore: BoosterStore;
  private isOpen = false;

  constructor(options: ShopScreenOptions) {
    this.options = options;
    this.boosterStore = options.boosterStore ?? new BoosterStore();
    this.overlay = document.createElement('div');
    this.overlay.className = 'shop-overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'ショップ');
    this.overlay.innerHTML = this.buildHTML();
    document.body.appendChild(this.overlay);
    this.wireEvents();
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.refreshBalance();
    this.refreshDailyButtons();
    this.refreshBoosterCounts();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
  }

  /** main.ts の addColor から呼ばれ、開いている時だけ残高表示を即時更新。
   *  ステージクリア報酬や Continue with Color 等、ショップ外の Color 変動を反映する。 */
  refreshBalanceIfOpen(): void {
    if (!this.isOpen) return;
    this.refreshBalance();
    this.refreshDailyButtons();
    this.refreshBoosterCounts();
  }

  // -------------------------------------------------------------------------
  // Build HTML
  // -------------------------------------------------------------------------

  private buildHTML(): string {
    const featuredPacksHTML = PACKS
      .filter((p) => p.featured !== null)
      .map((p) => this.buildFeaturedPackHTML(p))
      .join('');

    const standardPacksHTML = PACKS
      .filter((p) => p.featured === null)
      .map((p) => this.buildStandardRowHTML(p))
      .join('');

    return `
      <div class="shop-card">
        <div class="shop-header">
          <div class="shop-balance">🪙 <strong data-shop-balance>0</strong></div>
          <div class="shop-title">ショップ</div>
          <button class="shop-close" type="button" data-shop-close aria-label="閉じる">✕</button>
        </div>
        <div class="shop-demo-note">デモ:実購入は行われません(Phase A)</div>
        <div class="shop-body">

          <div class="shop-section">
            <div class="shop-section-title">無料で受け取る</div>
            <button class="shop-row shop-row--free" type="button" data-shop-item="daily">
              <span class="shop-row-icon">🎁</span>
              <div class="shop-row-info">
                <span class="shop-row-name">デイリーボーナス</span>
                <span class="shop-row-reward">+200 Color</span>
              </div>
              <span class="shop-row-cta" data-daily-cta>受取る</span>
            </button>
            <button class="shop-row shop-row--free" type="button" data-shop-item="ad">
              <span class="shop-row-icon">📺</span>
              <div class="shop-row-info">
                <span class="shop-row-name">動画を見る</span>
                <span class="shop-row-reward">+500 Color + ヘルパー×1</span>
              </div>
              <span class="shop-row-cta" data-ad-cta>視聴</span>
            </button>
          </div>

          ${featuredPacksHTML}

          <div class="shop-section">
            <div class="shop-section-title">Color パック</div>
            ${standardPacksHTML}
          </div>

          <div class="shop-section">
            <div class="shop-section-title">ブースター (各 ${BOOSTER_SHOP_PRICE} Color)</div>
            ${this.buildBoosterSectionHTML()}
          </div>

        </div>
      </div>
    `;
  }

  private buildFeaturedPackHTML(p: PackDef): string {
    const extraText = p.helperCount !== null
      ? `各ヘルパー ×${p.helperCount}`
      : '';
    return `
      <div class="shop-pack shop-pack--${p.featured ?? ''}" data-shop-item="${p.id}">
        <div class="shop-pack-badge">${p.badgeLabel ?? ''}</div>
        <div class="shop-pack-body">
          <div class="shop-pack-content">
            <div class="shop-pack-coins">${p.icon} ${p.colorAmount.toLocaleString('ja-JP')}</div>
            <div class="shop-pack-extras">${extraText}</div>
            <div class="shop-pack-name">${p.name}</div>
          </div>
          <button class="shop-pack-cta" type="button" data-pack-buy="${p.id}">${p.price}</button>
        </div>
      </div>
    `;
  }

  private buildStandardRowHTML(p: PackDef): string {
    return `
      <button class="shop-row" type="button" data-shop-item="${p.id}" data-pack-buy="${p.id}">
        <span class="shop-row-icon">${p.icon}</span>
        <div class="shop-row-info">
          <span class="shop-row-amount">${p.colorAmount.toLocaleString('ja-JP')}</span>
          <span class="shop-row-name">${p.name}</span>
        </div>
        <span class="shop-row-cta">${p.price}</span>
      </button>
    `;
  }

  private buildBoosterSectionHTML(): string {
    return BOOSTER_IDS.map((id) => {
      const meta = BOOSTER_META[id];
      const count = this.boosterStore.count(id);
      return `
        <button class="shop-booster-row" type="button" data-booster-buy="${id}">
          <span class="shop-booster-row-icon">${meta.icon}</span>
          <div class="shop-booster-row-info">
            <span class="shop-booster-row-name">${meta.name}</span>
            <span class="shop-booster-row-desc">${meta.descJa}</span>
          </div>
          <span class="shop-booster-row-count" data-booster-shop-count="${id}">残 ${count}</span>
          <span class="shop-booster-row-price">🪙 ${BOOSTER_SHOP_PRICE}</span>
        </button>
      `;
    }).join('');
  }

  // -------------------------------------------------------------------------
  // Wire Events
  // -------------------------------------------------------------------------

  private wireEvents(): void {
    // Backdrop click → close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
        this.options.onClose();
      }
    });

    // Close button
    this.overlay
      .querySelector<HTMLButtonElement>('[data-shop-close]')
      ?.addEventListener('click', () => {
        this.close();
        this.options.onClose();
      });

    // Daily bonus
    this.overlay
      .querySelector<HTMLButtonElement>('[data-shop-item="daily"]')
      ?.addEventListener('click', () => void this.handleDaily());

    // Watch ad
    this.overlay
      .querySelector<HTMLButtonElement>('[data-shop-item="ad"]')
      ?.addEventListener('click', () => void this.handleAd());

    // Pack buy buttons (featured inner button + standard row button)
    this.overlay
      .querySelectorAll<HTMLButtonElement>('[data-pack-buy]')
      .forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const id = btn.dataset.packBuy;
          if (!id) return;
          const pack = PACKS.find((p) => p.id === id);
          if (!pack) return;
          void this.handlePackPurchase(pack);
        });
      });

    // Booster individual buy buttons
    this.overlay
      .querySelectorAll<HTMLButtonElement>('[data-booster-buy]')
      .forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.boosterBuy as BoosterId | undefined;
          if (!id || !BOOSTER_IDS.includes(id)) return;
          void this.handleBoosterPurchase(id);
        });
      });
  }

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  private async handleDaily(): Promise<void> {
    if (this.isClaimedToday(LS_DAILY_SHOP)) return;
    this.options.addColor(200);
    this.markClaimedToday(LS_DAILY_SHOP);
    this.refreshBalance();
    this.refreshDailyButtons();
    this.showToast('+200 🪙 デイリーボーナス!');
  }

  private async handleAd(): Promise<void> {
    if (this.isClaimedToday(LS_AD_SHOP)) return;
    const { onWatchAd } = this.options;
    if (!onWatchAd) {
      // Ad overlay not configured — grant directly for demo
      this.grantAdReward();
      return;
    }
    const ok = await onWatchAd();
    if (!ok) return;
    this.grantAdReward();
  }

  private grantAdReward(): void {
    this.options.addColor(500);
    const id = HELPER_IDS[Math.floor(Math.random() * HELPER_IDS.length)] as HelperId;
    this.options.inventoryStore.add(id, 1);
    const meta = HELPER_META[id];
    this.markClaimedToday(LS_AD_SHOP);
    this.refreshBalance();
    this.refreshDailyButtons();
    this.showToast(`+500 🪙 + ${meta.icon} ${meta.name}!`);
  }

  private async handleBoosterPurchase(id: BoosterId): Promise<void> {
    const meta = BOOSTER_META[id];
    const balance = this.options.getColorBalance();
    if (balance < BOOSTER_SHOP_PRICE) {
      this.showToast(`Color が足りません (${BOOSTER_SHOP_PRICE} 必要)`);
      return;
    }
    const confirmed = await showConfirmDialog({
      title: meta.name,
      message: `Phase A デモ:実購入は発生しません。<br>${meta.icon} ${meta.name} を ${BOOSTER_SHOP_PRICE} Color で購入しますか?`,
    });
    if (!confirmed) return;
    this.options.addColor(-BOOSTER_SHOP_PRICE);
    this.boosterStore.add(id, 1);
    this.refreshBalance();
    this.refreshBoosterCounts();
    this.showToast(`${meta.icon} ${meta.name} +1 !`);
  }

  private refreshBoosterCounts(): void {
    for (const id of BOOSTER_IDS) {
      const el = this.overlay.querySelector<HTMLElement>(`[data-booster-shop-count="${id}"]`);
      if (el) el.textContent = `残 ${this.boosterStore.count(id)}`;
    }
  }

  private async handlePackPurchase(pack: PackDef): Promise<void> {
    const helperLine = pack.helperCount !== null
      ? `<br>各ヘルパー ×${pack.helperCount} も付与されます。`
      : '';
    const confirmed = await showConfirmDialog({
      title: pack.name,
      message: `Phase A デモ:実購入は発生しません。<br>🪙 ${pack.colorAmount.toLocaleString('ja-JP')} Color を付与しますか?${helperLine}`,
    });
    if (!confirmed) return;

    this.options.addColor(pack.colorAmount);

    if (pack.helperCount !== null) {
      for (const id of HELPER_IDS) {
        this.options.inventoryStore.add(id, pack.helperCount);
      }
      for (const id of BOOSTER_IDS) {
        this.boosterStore.add(id, pack.helperCount);
      }
    }

    this.refreshBalance();
    this.refreshBoosterCounts();
    const helperSuffix = pack.helperCount !== null
      ? ` + 各ヘルパー/ブースター ×${pack.helperCount}`
      : '';
    this.showToast(`+${pack.colorAmount.toLocaleString('ja-JP')} 🪙${helperSuffix}`);
  }

  // -------------------------------------------------------------------------
  // Toast
  // -------------------------------------------------------------------------

  private showToast(message: string, durationMs = 1800): void {
    let toast = this.overlay.querySelector<HTMLElement>('[data-shop-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'shop-toast';
      toast.setAttribute('data-shop-toast', '');
      this.overlay.querySelector('.shop-card')?.appendChild(toast);
    }
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.add('is-visible');
    window.clearTimeout((toast as unknown as { _t?: number })._t);
    (toast as unknown as { _t?: number })._t = window.setTimeout(() => {
      if (!toast) return;
      toast.classList.remove('is-visible');
      window.setTimeout(() => { if (toast) toast.hidden = true; }, 200);
    }, durationMs);
  }

  // -------------------------------------------------------------------------
  // State helpers
  // -------------------------------------------------------------------------

  private refreshBalance(): void {
    const el = this.overlay.querySelector<HTMLElement>('[data-shop-balance]');
    if (el) {
      el.textContent = this.options.getColorBalance().toLocaleString('ja-JP');
    }
  }

  private refreshDailyButtons(): void {
    const dailyBtn = this.overlay.querySelector<HTMLButtonElement>('[data-shop-item="daily"]');
    const adBtn = this.overlay.querySelector<HTMLButtonElement>('[data-shop-item="ad"]');
    const dailyCta = this.overlay.querySelector<HTMLElement>('[data-daily-cta]');
    const adCta = this.overlay.querySelector<HTMLElement>('[data-ad-cta]');

    const dailyClaimed = this.isClaimedToday(LS_DAILY_SHOP);
    const adClaimed = this.isClaimedToday(LS_AD_SHOP);

    if (dailyBtn) dailyBtn.disabled = dailyClaimed;
    if (adBtn) adBtn.disabled = adClaimed;
    if (dailyCta) dailyCta.textContent = dailyClaimed ? '受取済み' : '受取る';
    if (adCta) adCta.textContent = adClaimed ? '視聴済み' : '視聴';
  }

  private isClaimedToday(key: string): boolean {
    const today = new Date().toISOString().slice(0, 10);
    try {
      return window.localStorage.getItem(key) === today;
    } catch {
      return false;
    }
  }

  private markClaimedToday(key: string): void {
    const today = new Date().toISOString().slice(0, 10);
    try {
      window.localStorage.setItem(key, today);
    } catch {
      /* noop */
    }
  }
}
